<template>
  <sidebar-layout-item class="cmp-product-info">
    <template v-if="productInfo.usecase === CF_USECASE.SERVER">
      <div
        class="server-case row q-col-gutter-md justify-between"
        :class="[desktop ? 'q-py-xl q-px-5xl' : 'q-pt-lg q-px-lg q-pb-xl']"
      >
        <div class="info-wrap col-shrink">
          <h5 class="q-mb-xs">
            {{ $t(`scf.${productInfo.usecase}_info.title`) }}
          </h5>
          <p class="q-mb-none text-ws-base110 ws-typo-xs">
            <span>{{ productInfo.cpuName }}</span>, <span>{{ productInfo.ram }}GB</span>,
            <span>{{ productInfo.primaryDrives }}</span>
          </p>
        </div>
        <div
          v-if="productInfo.usecase === CF_USECASE.SERVER"
          class="free-items row justify-between items-start"
        >
          <div class="free-item q-mr-3xl">
            <ws-icon name="on" :size="32" class="float-left q-mr-sm" />
            <span class="ws-typo-xs">
              <strong>
                {{ $t('scf.server_info.power', { branches: 2, }) }}
              </strong>
            </span>
          </div>

          <div class="free-item">
            <ws-icon name="ethernet_on" :size="32" class="float-left q-mr-sm" />
            <span class="ws-typo-xs">
              <strong>
                {{ $t('scf.server_info.network', { connections: 2, }) }}
              </strong>
            </span>
          </div>
        </div>

        <div
          class="q-mt-xl row justify-between items-center full-width q-pt-none"
        >
          <div class="col-7">
            <ws-select
              v-model="selectedPeriod"
              :options="periodOptions"
              map-options
            >
              <template
                v-for="slotName in ['option', 'selected-item']"
                :slot="slotName"
                slot-scope="scope"
              >
                <q-item
                  :key="slotName + scope.index"
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{
                        $t('wsk.general.period.months', {
                          period: scope.opt,
                        })
                      }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </ws-select>
          </div>
          <price-period
            class="product-price"
            :prices="productInfo.prices"
            :period="selectedPeriod"
            :period-under="productInfo.usecase === CF_USECASE.SERVER"
            :verbose="productInfo.usecase === CF_USECASE.SERVER"
          />
        </div>
      </div>
    </template>

    <!-- Currently just CF_USECASE.MONITORING -->
    <template v-else>
      <div
        class="others-case row items-center justify-between q-col-gutter-x-md"
        :class="{
          'q-py-xl q-px-5xl': desktop,
          'q-pa-lg': tablet,
          'q-pa-md': mobile,
        }"
      >
        <h5 class="col q-mb-none">
          {{ $t(`scf.${productInfo.usecase}_info.title`) }}
        </h5>
        <price-period
          class="product-price float-right"
          from
          verbose
          :prices="productInfo.prices"
          :period="selectedPeriod"
        />
      </div>
    </template>
  </sidebar-layout-item>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { QItem, QItemLabel, QItemSection } from 'quasar';

import { WsSelect } from '@WS_Components/WsSelect.vue';
import {WsIcon} from '@loopia-group/components-vue2';
import SidebarLayoutItem from '@/components/SidebarLayoutItem.vue';
import PricePeriod from '@/components/PricePeriod.vue';
import { CF_USECASE } from '@/components/contactForm/const.enum';

interface Price {
  actualPrice: number;
  marketingPrice: number;
  period: number;
}

@Component({
  components: {
    QItem,
    QItemLabel,
    QItemSection,
    WsIcon,
    WsSelect,
    PricePeriod,
    SidebarLayoutItem,
  },
})
export default class ProductInfo extends Vue {
  @Prop({ default: () => ({ prices: {} }) })
  readonly productInfo!: any;
  @Prop({ type: Number, default: 1 })
  readonly period!: number;
  selectedPeriod = 1;

  data() {
    return {
      CF_USECASE,
    };
  }

  get periodOptions(): number[] {
    return this.productInfo.prices.map((price: Price) => price.period);
  }

  @Watch('period', { immediate: true })
  onPeriodChanged(period: number) {
    this.selectedPeriod = period;
  }

  @Watch('selectedPeriod')
  onSelectedPeriodChanged(period: number) {
    if (period !== this.period) {
      this.$emit('period-change', period);
    }
  }
}
</script>

<style lang="scss" scoped>
.cmp-product-info {
  padding: 0;
  border: $ws-border-thick;
  border-radius: $ws-border-radius;

  .q-item {
    min-height: 0;
    padding: 0;
  }

  > .server-case .product-price ::v-deep {
    .price strong {
      @include ws-typo-heading6;
      display: block;
    }
    .period {
      display: block;
    }
  }

  > .others-case > .product-price ::v-deep {
    text-align: right;
    .price strong {
      @include ws-typo-paragraph;
    }
  }

  .free-items {
    color: $ws-base150;
    margin-top: map-get($space-sm, y);

    > .free-item {
      max-width: 100px;
      display: flex;
      align-items: center;
    }
  }
}
</style>
